import React from 'react';
import {FormattedMessage} from 'react-intl';
import {graphql, usePaginationFragment} from 'react-relay/hooks';
import classNames from 'classnames';
import Button from '../../../components/button/button';
import PostItem from './post-item/post-item';

const ForMePostsList = React.memo(({queryRef, currentUserId, isAuthorized, highlightId, nextCount = 8, showMoreButton = true}) => {
    const {data, loadNext, hasNext, isLoadingNext, refetch} = usePaginationFragment(
        graphql`
            fragment forMePostsList_query on Query
            @refetchable(queryName: "ForMePostsListQuery")
            @argumentDefinitions(
                first: {type: "Int", defaultValue: 8}
                after: {type: "String"}
                includeIsAuthorized: {type: "Boolean", defaultValue: false}
            ) {
                forMePosts(first: $first, after: $after) @connection(key: "ForMePostsList_forMePosts") {
                    edges {
                            node {
                                id
                                message
                                media
                                created
                                removed
                                spam
                            likesCount
                            likedByCurrentUser @include(if: $includeIsAuthorized)
                            stemLikesCount
                            stemLikedByCurrentUser @include(if: $includeIsAuthorized)
                            viewsCount
                            viewedByCurrentUser @include(if: $includeIsAuthorized)
                            commentedByCurrentUser @include(if: $includeIsAuthorized)
                            quotedByCurrentUser @include(if: $includeIsAuthorized)
                            commentsCount
                            quotesCount
                            sharesCount
                            sharedByCurrentUser @include(if: $includeIsAuthorized)
                            poll {
                                id
                                options
                                optionVotesCounts
                                pollVotesCount
                                votedByCurrentUser
                                votedOptionIndex
                            }
                            sharedPost {
                                id
                                message
                                media
                                created
                                removed
                                spam
                                likesCount
                                likedByCurrentUser @include(if: $includeIsAuthorized)
                                stemLikesCount
                                stemLikedByCurrentUser @include(if: $includeIsAuthorized)
                                viewsCount
                                viewedByCurrentUser @include(if: $includeIsAuthorized)
                                commentedByCurrentUser @include(if: $includeIsAuthorized)
                                quotedByCurrentUser @include(if: $includeIsAuthorized)
                                commentsCount
                                quotesCount
                                sharesCount
                                poll {
                                    id
                                    options
                                    optionVotesCounts
                                    pollVotesCount
                                    votedByCurrentUser
                                    votedOptionIndex
                                }
                                author {
                                    id
                                    vibe {
                                        username
                                    }
                                    followingUser @include(if: $includeIsAuthorized)
                                }
                            }
                            quotedPost {
                                id
                                message
                                media
                                created
                                removed
                                spam
                                likesCount
                                likedByCurrentUser @include(if: $includeIsAuthorized)
                                stemLikesCount
                                stemLikedByCurrentUser @include(if: $includeIsAuthorized)
                                viewsCount
                                viewedByCurrentUser @include(if: $includeIsAuthorized)
                                commentedByCurrentUser @include(if: $includeIsAuthorized)
                                quotedByCurrentUser @include(if: $includeIsAuthorized)
                                quotesCount
                                sharesCount
                                poll {
                                    id
                                    options
                                    optionVotesCounts
                                    pollVotesCount
                                    votedByCurrentUser
                                    votedOptionIndex
                                }
                                author {
                                    id
                                    vibe {
                                        username
                                    }
                                }
                            }
                            author {
                                id
                                vibe {
                                    username
                                }
                                followingUser @include(if: $includeIsAuthorized)
                            }
                        }
                    }
                    pageInfo {
                        hasNextPage
                        endCursor
                    }
                }
            }
        `,
        queryRef
    );
    
    const [isPending, startTransition] = React.useTransition();
    
    const posts = data.forMePosts?.edges || [];
    const postRefs = React.useRef(new Map());

    const getPostRef = React.useCallback((postId) => {
        if (!postRefs.current.has(postId)) {
            postRefs.current.set(postId, React.createRef());
        }
        return postRefs.current.get(postId);
    }, []);

    return (
        <div className={classNames({'opacity-0dot4': isPending})}>
            {posts.length === 0 && (
                <div className='text-md color-gray-500 padding-left-0dot75rem'>
                    <FormattedMessage defaultMessage='No posts for you yet' />
                </div>
            )}
            {posts.length > 0 && (
                <div>
                    {posts.map((edge, index) => {
                        const post = edge.node;
                        return (
                            <div key={post.id} className={index > 0 ? 'padding-top-1rem' : ''}>
                                <PostItem
                                    post={post}
                                    userId={post.author?.id}
                                    currentUserId={currentUserId}
                                    isAuthorized={isAuthorized}
                                    isFollowing={post.author?.followingUser || false}
                                    getPostRef={getPostRef}
                                    highlightId={highlightId}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
            {showMoreButton && hasNext && (
                <div className='padding-top-1rem display-flex justify-content-center'>
                    <Button {...{
                        size: 'md',
                        color: 'primary',
                        clickHandler: () => loadNext(nextCount),
                        isLoading: isLoadingNext,
                        disabled: isLoadingNext
                    }}>
                        <FormattedMessage defaultMessage='More posts' />
                    </Button>
                </div>
            )}
        </div>
    );
});

export default ForMePostsList;

